import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, RefreshCw, Copy, Check } from "lucide-react";

type AppRole = "super_admin" | "admin" | "editor" | "viewer" | "support_manager";
type Status = "active" | "pending" | "suspended";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function genPwd(len = 14) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

const CreateTeamMemberDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const { toast } = useToast();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(genPwd());
  const [role, setRole] = useState<AppRole>("editor");
  const [status, setStatus] = useState<Status>("active");
  const [phone, setPhone] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ email: string; password: string; sent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setFullName(""); setEmail(""); setPassword(genPwd()); setRole("editor");
    setStatus("active"); setPhone(""); setAvatarFile(null); setResult(null); setCopied(false);
  };

  const submit = async () => {
    if (!email.includes("@")) { toast({ variant: "destructive", title: "Enter a valid email" }); return; }
    if (password.length < 8) { toast({ variant: "destructive", title: "Password must be 8+ chars" }); return; }
    setSubmitting(true);
    try {
      let avatarUrl: string | null = null;
      if (avatarFile) {
        const path = `team/${Date.now()}-${avatarFile.name.replace(/[^a-zA-Z0-9.\-_]/g, "_")}`;
        const { error: upErr } = await supabase.storage.from("avatars").upload(path, avatarFile, { upsert: true });
        if (!upErr) {
          const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
          avatarUrl = pub.publicUrl;
        }
      }

      const { data, error } = await supabase.functions.invoke("admin-create-team-member", {
        body: {
          full_name: fullName,
          email: email.trim().toLowerCase(),
          password,
          role,
          status,
          phone: phone || null,
          avatar_url: avatarUrl,
          login_url: `${window.location.origin}/admin/login`,
        },
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error ?? error?.message ?? "Failed");
      }
      setResult({ email: (data as any).email, password: (data as any).temp_password, sent: !!(data as any).email_sent });
      toast({ title: "Team member created", description: (data as any).email_sent ? "Credentials emailed." : "Copy credentials below." });
      onCreated();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Failed to create", description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const copyAll = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Email: ${result.email}\nPassword: ${result.password}\nLogin: ${window.location.origin}/admin/login`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{result ? "Team Member Created" : "Create Team Member"}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-muted/40 space-y-2 text-sm">
              <div><span className="text-muted-foreground">Email:</span> <span className="font-mono">{result.email}</span></div>
              <div><span className="text-muted-foreground">Temporary password:</span> <span className="font-mono break-all">{result.password}</span></div>
              <div><span className="text-muted-foreground">Login URL:</span> <span className="font-mono break-all">{window.location.origin}/admin/login</span></div>
            </div>
            <p className="text-sm text-muted-foreground">
              {result.sent ? "Credentials were emailed to the user." : "Email delivery failed — share these credentials manually."}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={copyAll}>
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copied" : "Copy credentials"}
              </Button>
              <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fn">Full name</Label>
                <Input id="fn" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="ph">Phone (optional)</Label>
                <Input id="ph" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="em">Email</Label>
              <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <div>
              <Label htmlFor="pw">Temporary password</Label>
              <div className="flex gap-2">
                <Input id="pw" value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" />
                <Button type="button" variant="outline" size="icon" onClick={() => setPassword(genPwd())} title="Regenerate">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">User will be required to change this on first login.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={role} onValueChange={(v) => setRole(v as AppRole)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="support_manager">Support Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="av">Profile photo (optional)</Label>
              <Input id="av" type="file" accept="image/*" onChange={(e) => setAvatarFile(e.target.files?.[0] ?? null)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateTeamMemberDialog;
